import { useAuth } from "../../context/AuthContext";
import { useState } from "react";
import { X, Search, RotateCcw, Download } from "lucide-react";
import { Dropdown } from "../../components/ui/dropdown";
import { CopyButton } from "../../components/ui/copy-button";
import { useArchive, useRetryFromArchive, exportResults } from "./queries";
import type { Batch } from "./types";
import { formatBatchDateTime } from "./batch-time";
import { cn } from "../../lib/utils";

export function ArchiveDrawer({ 
  isOpen, 
  onClose,
  initialStatus = "all",
  workflowId,
  onSelect,
}: { 
  isOpen: boolean; 
  onClose: () => void;
  initialStatus?: string;
  workflowId?: string;
  onSelect?: (batch: Batch) => void;
}) {
  const { user } = useAuth() as any;
  const globalTz = user?.timezone;
  const [status, setStatus] = useState(initialStatus);
  const [q, setQ] = useState("");
  const { data, isLoading } = useArchive({ from: "", to: "", status, q, workflow_id: workflowId }, isOpen);
  const retryMutation = useRetryFromArchive();
  const qLower = q.trim().toLowerCase();
  const visibleItems = (data?.items || []).filter((batch) => {
    if (!qLower) return true;
    return (
      batch.name?.toLowerCase().includes(qLower) ||
      batch.schedule_id?.toLowerCase().includes(qLower)
    );
  });

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed top-0 right-0 bottom-0 w-[600px] bg-canvas border-l border-line z-50 flex flex-col shadow-2xl">
        <div className="p-6 border-b border-line flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-ink">Archive</h2>
            <p className="text-sm text-ink-3">Completed and paused over 7 days, plus failed and cancelled batches</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-soft rounded-full text-ink-3 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 border-b border-line flex gap-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
            <input 
              type="text" 
              placeholder="Search batches..." 
              value={q}
              onChange={e => setQ(e.target.value)}
              className="w-full bg-elevated border border-line rounded-lg pl-9 pr-4 py-2 text-sm text-ink focus:outline-none focus:border-brand/50"
            />
          </div>
          <Dropdown
            ariaLabel="Status Filter"
            className="w-40"
            value={status} 
            onChange={setStatus}
            options={[
              { value: "all", label: "All Statuses" },
              { value: "completed", label: "Completed" },
              { value: "paused", label: "Paused" },
              { value: "failed", label: "Failed" },
              { value: "cancelled", label: "Cancelled" },
            ]}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4 custom-scrollbar">
          {isLoading ? (
            <div className="text-center text-ink-3 py-12">Loading archive...</div>
          ) : !visibleItems.length ? (
            <div className="text-center text-ink-3 py-12">No batches found in archive.</div>
          ) : (
            visibleItems.map(batch => (
              <div
                key={batch.schedule_id}
                className={cn(
                  "bg-elevated border border-line p-4 rounded-xl flex flex-col gap-3 group transition-colors",
                  onSelect && "cursor-pointer hover:border-brand/30",
                )}
                onClick={() => onSelect?.(batch)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-ink">{batch.name}</h4>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="text-xs text-ink-3">
                        {formatBatchDateTime(
                          batch.finished_at || batch.status_at,
                          globalTz,
                        ) || "Unknown date"}
                      </span>
                      <span className="text-xs text-ink-3 font-mono" title={batch.schedule_id}>
                        · ID: {batch.schedule_id}
                      </span>
                      <CopyButton value={batch.schedule_id} />
                    </div>
                  </div>
                  <span className={`capitalize px-2 py-1 rounded-full text-[10px] font-medium border ${
                    batch.status === "completed" ? "bg-soft text-ink border-line" :
                    batch.status === "failed" ? "bg-danger-soft text-danger border-danger/20" :
                    "bg-soft text-ink-3 border-line"
                  }`}>
                    {batch.status}
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-4 py-3 border-y border-line text-center text-xs">
                  <div>
                    <div className="text-ink-3 mb-1">Total</div>
                    <div className="font-medium text-ink">{batch.total}</div>
                  </div>
                  <div>
                    <div className="text-ink-3 mb-1">Completed</div>
                    <div className="font-medium text-ink">{batch.completed}</div>
                  </div>
                  <div>
                    <div className="text-ink-3 mb-1">Not picked</div>
                    <div className="font-medium text-danger">{batch.failed}</div>
                  </div>
                  <div>
                    <div className="text-ink-3 mb-1">Success %</div>
                    <div className="font-medium text-ink">
                      {batch.total ? Math.round((batch.completed / batch.total) * 100) : 0}%
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-1" onClick={(e) => e.stopPropagation()}>
                  <button 
                    onClick={() => exportResults(batch)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-ink-3 hover:text-ink hover:bg-soft transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Export Data
                  </button>
                  {batch.status === "failed" && (
                    <button 
                      onClick={() => { retryMutation.mutate(batch); onClose(); }}
                      disabled={retryMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-soft text-brand hover:bg-brand/20 transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Retry Failed
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
